import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Product, InventoryTransaction } from '../types';

export class InventoryService {
  // Add new product
  static async addProduct(
    salonId: string,
    productData: Omit<Product, 'id' | 'salonId' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'products'), {
        ...productData,
        salonId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      return docRef.id;
    } catch (error) {
      console.error('Error adding product:', error);
      throw new Error('Failed to add product');
    }
  }

  // Update product
  static async updateProduct(
    productId: string,
    updates: Partial<Omit<Product, 'id' | 'salonId' | 'createdAt'>>
  ): Promise<void> {
    try {
      const productRef = doc(db, 'products', productId);
      await updateDoc(productRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating product:', error);
      throw new Error('Failed to update product');
    }
  }

  // Get all products for a salon
  static async getProducts(salonId: string): Promise<Product[]> {
    try {
      const q = query(
        collection(db, 'products'),
        where('salonId', '==', salonId)
      );

      const querySnapshot = await getDocs(q);
      const products: Product[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        products.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        } as Product);
      });

      // Sort client-side instead of requiring composite index
      return products.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Error getting products:', error);
      throw new Error('Failed to get products');
    }
  }

  // Get product by ID
  static async getProduct(productId: string): Promise<Product | null> {
    try {
      const docRef = doc(db, 'products', productId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        } as Product;
      }
      return null;
    } catch (error) {
      console.error('Error getting product:', error);
      throw new Error('Failed to get product');
    }
  }

  // Add stock to product (purchase/restock)
  static async addStock(
    productId: string,
    quantity: number,
    unitPrice?: number,
    notes?: string
  ): Promise<void> {
    try {
      const product = await this.getProduct(productId);
      if (!product) throw new Error('Product not found');

      const newStock = product.currentStock + quantity;

      // Update product stock
      await this.updateProduct(productId, { currentStock: newStock });

      // Record inventory transaction
      await this.recordTransaction({
        salonId: product.salonId,
        productId,
        type: 'purchase',
        quantity,
        unitPrice,
        totalValue: unitPrice ? unitPrice * quantity : undefined,
        notes,
      });

    } catch (error) {
      console.error('Error adding stock:', error);
      throw new Error('Failed to add stock');
    }
  }

  // Record inventory transaction
  private static async recordTransaction(
    transaction: Omit<InventoryTransaction, 'id' | 'createdAt'>
  ): Promise<void> {
    try {
      // Filter out undefined values to prevent Firebase errors
      const cleanTransaction = Object.fromEntries(
        Object.entries(transaction).filter(([_, value]) => value !== undefined)
      );

      await addDoc(collection(db, 'inventoryTransactions'), {
        ...cleanTransaction,
        createdAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error recording transaction:', error);
    }
  }

  // Adjust stock (manual adjustment)
  static async adjustStock(
    productId: string,
    quantity: number,
    reason: string
  ): Promise<void> {
    try {
      const product = await this.getProduct(productId);
      if (!product) throw new Error('Product not found');

      const newStock = product.currentStock + quantity;

      // Update product stock
      await this.updateProduct(productId, { currentStock: Math.max(0, newStock) });

      // Record inventory transaction
      await this.recordTransaction({
        salonId: product.salonId,
        productId,
        type: 'adjustment',
        quantity,
        notes: reason,
      });

    } catch (error) {
      console.error('Error adjusting stock:', error);
      throw new Error('Failed to adjust stock');
    }
  }

  // Get low stock products
  static async getLowStockProducts(salonId: string): Promise<Product[]> {
    try {
      const products = await this.getProducts(salonId);
      return products.filter(product =>
        product.currentStock <= product.minStockLevel && product.isActive
      );
    } catch (error) {
      console.error('Error getting low stock products:', error);
      throw new Error('Failed to get low stock products');
    }
  }

  // Get inventory transactions for a product
  static async getProductTransactions(
    productId: string,
    limit: number = 50
  ): Promise<InventoryTransaction[]> {
    try {
      const q = query(
        collection(db, 'inventoryTransactions'),
        where('productId', '==', productId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const transactions: InventoryTransaction[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        transactions.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt.toDate(),
        } as InventoryTransaction);
      });

      return transactions.slice(0, limit);
    } catch (error) {
      console.error('Error getting product transactions:', error);
      throw new Error('Failed to get product transactions');
    }
  }

  // Delete product (soft delete by setting isActive to false)
  static async deleteProduct(productId: string): Promise<void> {
    try {
      await this.updateProduct(productId, { isActive: false });
    } catch (error) {
      console.error('Error deleting product:', error);
      throw new Error('Failed to delete product');
    }
  }
}
